import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Package, TrendingUp, AlertTriangle, Download, UserPlus, Activity, CalendarDays } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, formatPrice, formatDate, errText, ORDER_STATUS, API } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import ContentTab from "./admin/ContentTab";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Inbox, Mail, ListTodo, UserSearch, Flag, Scale, ShieldCheck, BarChart3, UserCog, Landmark, Percent, Award, Megaphone, Gift, FileText, History, FlaskConical, UsersRound, ExternalLink, LogOut } from "lucide-react";
import { ROLE_LABEL } from "./panel/common";
import PanelOverview from "./panel/PanelOverview";
import PanelTickets from "./panel/PanelTickets";
import PanelEmails from "./panel/PanelEmails";
import PanelTasks from "./panel/PanelTasks";
import PanelEmployees from "./panel/PanelEmployees";
import PanelCustomers from "./panel/PanelCustomers";

function TaxTab() {
  const year_now = new Date().getFullYear();
  const [year, setYear] = useState(year_now);
  const [country, setCountry] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => {
    const p = new URLSearchParams({ year });
    if (country !== "ALL") p.set("country", country);
    if (status !== "ALL") p.set("status", status);
    api.get(`/admin/tax/overview?${p}`).then(({ data }) => setData(data)).catch(() => setData(false));
  };
  useEffect(() => { load(); }, [year, country, status]);
  const review = async (uid, action) => {
    const note = action === "REJECTED" ? window.prompt("Hinweis für den Verkäufer (optional):") : null;
    setBusyId(uid);
    try { await api.post(`/admin/tax/review/${uid}`, { action, note }); toast.success(action === "VERIFIED" ? "Als geprüft markiert" : "Zur Korrektur zurückgegeben"); load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusyId(null); }
  };
  const exportCsv = async () => {
    try {
      const p = new URLSearchParams({ year });
      if (country !== "ALL") p.set("country", country);
      const res = await api.get(`/admin/tax/export?${p}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `kledio_dac7_${year}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export heruntergeladen");
    } catch (e) { toast.error(errText(e)); }
  };
  if (data === null) return <div className="h-40 bg-muted rounded-2xl animate-pulse" />;
  if (data === false) return <p className="text-sm text-muted-foreground">Steuerübersicht konnte nicht geladen werden (Admin-Rechte erforderlich).</p>;
  const STATUS_DE = { MISSING: "Fehlt", SUBMITTED: "Eingereicht", VERIFIED: "Geprüft", REJECTED: "Abgelehnt" };
  const sel = "rounded-xl border border-border bg-card px-3 py-2 text-sm";
  return (
    <div className="space-y-5" data-testid="admin-tax-tab">
      <div className="border border-amber-300 bg-amber-50 rounded-2xl p-3.5 text-xs text-amber-800">
        {data.disclaimer} Es erfolgt KEINE automatische Behördenmeldung — Prüfhinweise sind keine endgültige rechtliche Einstufung.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="tax-stat-sellers" label={`Verkäufer mit Aktivität/Daten (${data.summary.year})`} value={data.summary.sellers} />
        <StatCard testid="tax-stat-missing" label="Angaben fehlend/offen" value={(data.summary.by_status.MISSING || 0) + (data.summary.by_status.REJECTED || 0)} />
        <StatCard testid="tax-stat-submitted" label="Eingereicht, ungeprüft" value={data.summary.by_status.SUBMITTED || 0} />
        <StatCard testid="tax-stat-hints" label="Prüfhinweise Meldepflicht" value={data.summary.reporting_hints} />
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <select data-testid="tax-filter-year" className={sel} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[year_now, year_now - 1, year_now - 2].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select data-testid="tax-filter-country" className={sel} value={country} onChange={(e) => setCountry(e.target.value)}>
          <option value="ALL">Alle Länder</option><option value="DE">Deutschland (PStTG)</option>
          <option value="AT">Österreich (DPMG)</option><option value="CH">Schweiz</option>
        </select>
        <select data-testid="tax-filter-status" className={sel} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ALL">Alle Status</option>
          {Object.entries(STATUS_DE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button data-testid="tax-export-btn" onClick={exportCsv}
          className="ml-auto bg-primary text-primary-foreground text-sm font-bold rounded-full px-4 py-2 flex items-center gap-2 hover:opacity-90">
          <Download className="w-4 h-4" /> CSV für Steuerberater
        </button>
      </div>
      <div className="hidden lg:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1.4fr] gap-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground px-3">
        <span>Verkäufer</span><span>Land / Status</span><span>Verkäufe</span><span>Erlöse</span><span>Erstattungen</span><span>Auszahlungen</span><span>Aktion</span>
      </div>
      <div className="space-y-2">
        {data.rows.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Keine Verkäufer für diese Filter.</p>}
        {data.rows.map((r) => (
          <div key={r.user_id} data-testid={`tax-row-${r.user_id}`}
            className="border border-border rounded-2xl p-3.5 grid gap-2 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1.4fr] lg:items-center bg-card">
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{r.username} {r.is_demo && <span className="text-[10px] bg-secondary rounded-full px-1.5 py-0.5 ml-1">Demo</span>}
                {r.reporting_hint && <span className="text-[10px] bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5 ml-1" data-testid="tax-hint-badge">Prüfhinweis</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{r.email}{r.tin_masked ? ` · TIN ${r.tin_masked}` : ""}</p>
            </div>
            <div className="text-xs"><b>{r.country || "—"}</b> · {STATUS_DE[r.status] || r.status}</div>
            <div className="text-sm lg:text-center"><span className="lg:hidden text-xs text-muted-foreground">Verkäufe: </span>{r.stats.sales_count}</div>
            <div className="text-sm"><span className="lg:hidden text-xs text-muted-foreground">Erlöse: </span>{formatPrice(r.stats.gross_revenue)}</div>
            <div className="text-sm"><span className="lg:hidden text-xs text-muted-foreground">Erstattet: </span>{formatPrice(r.stats.refunds)}</div>
            <div className="text-sm"><span className="lg:hidden text-xs text-muted-foreground">Ausgezahlt: </span>{formatPrice(r.stats.payouts)}</div>
            <div className="flex gap-2">
              {r.status === "SUBMITTED" && (
                <>
                  <button data-testid={`tax-verify-${r.user_id}`} disabled={busyId === r.user_id} onClick={() => review(r.user_id, "VERIFIED")}
                    className="text-xs font-bold bg-green-600 text-white rounded-full px-3 py-1.5 hover:opacity-90 disabled:opacity-50">Geprüft ✓</button>
                  <button data-testid={`tax-reject-${r.user_id}`} disabled={busyId === r.user_id} onClick={() => review(r.user_id, "REJECTED")}
                    className="text-xs font-bold border border-red-300 text-red-600 rounded-full px-3 py-1.5 hover:bg-red-50 disabled:opacity-50">Zurückgeben</button>
                </>
              )}
              {r.status === "MISSING" && (
                <button data-testid={`tax-request-${r.user_id}`} disabled={busyId === r.user_id}
                  onClick={async () => { setBusyId(r.user_id); try { await api.post(`/admin/tax/request/${r.user_id}`); toast.success("Aufforderung gesendet (Benachrichtigung + E-Mail)"); } catch (e) { toast.error(errText(e)); } finally { setBusyId(null); } }}
                  className="text-xs font-bold bg-accent text-white rounded-full px-3 py-1.5 hover:opacity-90 disabled:opacity-50">Daten anfordern</button>
              )}
              {r.status !== "SUBMITTED" && r.status !== "MISSING" && <span className="text-xs text-muted-foreground">{STATUS_DE[r.status]}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-border rounded-2xl p-4 bg-card">
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

function UserStatsSection() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let active = true;
    const load = () => api.get("/admin/stats/users").then(({ data }) => active && setS(data)).catch(() => active && setS(false));
    load();
    const t = setInterval(load, 20000);
    return () => { active = false; clearInterval(t); };
  }, []);
  if (s === null) return <div className="h-40 bg-muted rounded-2xl animate-pulse" />;
  if (s === false) return <p className="text-sm text-muted-foreground">Nutzerstatistiken konnten nicht geladen werden.</p>;
  const fmtDay = (d) => `${d.slice(8, 10)}.${d.slice(5, 7)}.`;
  const regData = s.registrations_per_day.map((x) => ({ ...x, label: fmtDay(x.day) }));
  const dauData = s.dau_per_day.filter((x) => x.count != null).map((x) => ({ ...x, label: fmtDay(x.day) }));
  const cards = [
    { testid: "ustat-total", label: "Registrierte Nutzer gesamt", value: s.total_users, icon: Users },
    { testid: "ustat-today", label: "Neue Registrierungen heute", value: s.new_today, icon: UserPlus },
    { testid: "ustat-online", label: `Online jetzt (letzte ${s.online_window_minutes} Min.)`, value: s.has_presence_data ? s.online_now : "—", icon: Activity, live: true },
    { testid: "ustat-24h", label: "Aktive Nutzer letzte 24 Std.", value: s.has_presence_data ? s.active_24h : "—", icon: Activity },
    { testid: "ustat-week", label: "Neue Registrierungen diese Woche", value: s.new_week, icon: CalendarDays },
  ];
  return (
    <div className="border border-border rounded-2xl p-4 sm:p-5" data-testid="user-stats-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-base flex items-center gap-2"><Users className="w-4 h-4 text-accent" /> Nutzerstatistiken</h3>
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Live · alle 20 Sek.
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="border border-border rounded-2xl p-4 bg-card">
            <div className="flex items-center justify-between">
              <c.icon className="w-4 h-4 text-muted-foreground" />
              {c.live && s.has_presence_data && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
            </div>
            <p className="text-2xl font-bold mt-2">{c.value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{c.label}</p>
          </div>
        ))}
      </div>
      {!s.has_presence_data && (
        <p className="text-xs text-muted-foreground mt-3" data-testid="no-presence-hint">
          Noch keine Live-Aktivitätsdaten vorhanden — die Erfassung beginnt, sobald sich Nutzer anmelden und die Seite nutzen.
        </p>
      )}
      {s.demo_users > 0 && <p className="text-[11px] text-muted-foreground mt-2">Hinweis: {s.demo_users} Demo-Konten sind in den Zahlen nicht enthalten.</p>}
      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div data-testid="registrations-chart">
          <p className="text-sm font-bold mb-2">Registrierungen pro Tag (30 Tage)</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={regData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} tickLine={false} axisLine={{ stroke: "#ddd" }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => [v, "Registrierungen"]} labelFormatter={(l) => `Tag: ${l}`} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="count" fill="#FF6B35" radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div data-testid="dau-chart">
          <p className="text-sm font-bold mb-2">Täglich aktive Nutzer</p>
          {dauData.length === 0 ? (
            <div className="h-48 flex items-center justify-center border border-dashed border-border rounded-2xl text-xs text-muted-foreground text-center px-6">
              Noch keine Verlaufsdaten — die tägliche Aktivitätserfassung startet mit diesem Update.
            </div>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dauData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#ddd" }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => [v, "Aktive Nutzer"]} labelFormatter={(l) => `Tag: ${l}`} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#1a1a1a" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [finance, setFinance] = useState(null);
  const [period, setPeriod] = useState("30d");
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/finance", { params: { period } }).then(({ data }) => setFinance(data)).catch(() => {}); }, [period]);
  if (!stats) return <div className="h-40 bg-muted rounded-2xl animate-pulse" />;
  return (
    <div className="space-y-6">
      <UserStatsSection />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="admin-stat-users" label="Registrierte Nutzer" value={stats.total_users} />
        <StatCard testid="admin-stat-items" label="Aktive Artikel" value={stats.active_items} />
        <StatCard testid="admin-stat-sales" label="Verkäufe" value={stats.sales} />
        <StatCard testid="admin-stat-gmv" label="Handelsvolumen (GMV)" value={formatPrice(stats.gmv)} />
        <StatCard testid="admin-stat-revenue" label="Plattformumsatz" value={formatPrice(stats.platform_revenue)} />
        <StatCard testid="admin-stat-disputes" label="Offene Disputes" value={stats.open_disputes} />
        <StatCard testid="admin-stat-reports" label="Offene Meldungen" value={stats.open_reports} />
        <StatCard testid="admin-stat-kyc" label="KYC ausstehend" value={stats.kyc_pending} />
      </div>
      <div className="border border-border rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base">Finanzen</h3>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="finance-period-select" className="w-36 rounded-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Heute</SelectItem><SelectItem value="7d">7 Tage</SelectItem>
              <SelectItem value="30d">30 Tage</SelectItem><SelectItem value="month">Dieser Monat</SelectItem>
              <SelectItem value="year">Dieses Jahr</SelectItem><SelectItem value="all">Gesamt</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {finance && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="finance-metrics">
            <StatCard label="GMV" value={formatPrice(finance.gmv)} />
            <StatCard label="Plattformgebühren" value={formatPrice(finance.platform_revenue)} />
            <StatCard label="Refunds" value={formatPrice(finance.refunds)} />
            <StatCard label="Netto-Plattformumsatz" value={formatPrice(finance.net_platform_revenue)} />
            <StatCard label="Auszahlungen" value={formatPrice(finance.payouts_amount)} />
            <StatCard label="Transaktionen" value={finance.transactions} />
          </div>
        )}
        <div className="flex gap-3 mt-4">
          <a data-testid="export-finance-csv" href={`${API}/admin/exports/finance?fmt=csv`} className="text-xs font-bold border border-border rounded-full px-4 py-2 hover:bg-secondary flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Finanzexport CSV</a>
          <a data-testid="export-dac7-csv" href={`${API}/admin/exports/dac7?fmt=csv`} className="text-xs font-bold border border-border rounded-full px-4 py-2 hover:bg-secondary flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> DAC7-Report CSV</a>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/users", { params: { q } }).then(({ data }) => setUsers(data.users)).catch(() => {});
  }, [q]);
  useEffect(() => { load(); }, [load]);
  const [stripeInfo, setStripeInfo] = useState({});
  const loadStripe = async (id) => {
    if (stripeInfo[id]) { setStripeInfo({ ...stripeInfo, [id]: null }); return; }
    try {
      const { data } = await api.get(`/admin/sellers/${id}/stripe`);
      setStripeInfo({ ...stripeInfo, [id]: data });
    } catch (e) { toast.error(errText(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/users/${id}/status`, { status }); toast.success("Aktualisiert"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div>
      <input data-testid="admin-user-search" className="w-full max-w-sm rounded-full border border-border px-4 py-2.5 text-sm mb-4" placeholder="E-Mail oder Benutzername suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="border border-border rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-secondary/50 text-left text-xs"><tr><th className="p-3">Nutzer</th><th className="p-3">Rolle</th><th className="p-3">Status</th><th className="p-3">KYC</th><th className="p-3">Aktion</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <React.Fragment key={u.id}>
              <tr className="border-t border-border" data-testid={`admin-user-row-${u.id}`}>
                <td className="p-3"><p className="font-semibold">{u.username}</p><p className="text-xs text-muted-foreground">{u.email}</p></td>
                <td className="p-3 text-xs">{u.role}</td>
                <td className="p-3"><span className={`text-xs font-semibold px-2 py-1 rounded-md ${u.status === "BANNED" ? "bg-destructive text-white" : "bg-secondary"}`}>{u.status}</span></td>
                <td className="p-3 text-xs">{u.seller?.verification_status || "—"}</td>
                <td className="p-3 flex gap-2">
                  {u.status === "BANNED" ? (
                    <button data-testid={`unban-btn-${u.id}`} onClick={() => setStatus(u.id, "ACTIVE")} className="text-xs font-bold border border-border rounded-full px-3 py-1.5 hover:bg-secondary">Entsperren</button>
                  ) : (
                    <button data-testid={`ban-btn-${u.id}`} onClick={() => setStatus(u.id, "BANNED")} className="text-xs font-bold border border-destructive text-destructive rounded-full px-3 py-1.5 hover:bg-destructive/10">Sperren</button>
                  )}
                  <button data-testid={`stripe-info-btn-${u.id}`} onClick={() => loadStripe(u.id)} className="text-xs font-bold border border-border rounded-full px-3 py-1.5 hover:bg-secondary">Stripe</button>
                </td>
              </tr>
              {stripeInfo[u.id] && (
                <tr className="border-t border-border bg-secondary/30" data-testid={`stripe-info-row-${u.id}`}>
                  <td colSpan={5} className="p-3 text-xs space-y-1">
                    <p><b>Account:</b> {stripeInfo[u.id].stripe_account_id || "nicht verbunden"} · <b>Status:</b> {stripeInfo[u.id].payout_state}</p>
                    <p>Zahlungen: {stripeInfo[u.id].charges_enabled ? "✅" : "❌"} · Auszahlungen: {stripeInfo[u.id].payouts_enabled ? "✅" : "❌"} · Transfers: {stripeInfo[u.id].transfers_active ? "✅" : "❌"} · Bankkonto: {stripeInfo[u.id].external_account_set ? "✅" : "❌"}</p>
                    {stripeInfo[u.id].requirements_currently_due?.length > 0 && <p className="text-amber-700">Offene Anforderungen: {stripeInfo[u.id].requirements_currently_due.join(", ")}</p>}
                    {stripeInfo[u.id].disabled_reason && <p className="text-red-600">Einschränkung: {stripeInfo[u.id].disabled_reason}</p>}
                    {Object.keys(stripeInfo[u.id].open_payouts || {}).length > 0 && <p><b>Offene Auszahlungen:</b> {Object.entries(stripeInfo[u.id].open_payouts).map(([k, v]) => `${k}: ${v.count}× (${v.amount.toFixed(2)} €)`).join(" · ")}</p>}
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const load = useCallback(() => { api.get("/admin/orders").then(({ data }) => setOrders(data.orders)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const refund = async (o) => {
    try { await api.post(`/admin/orders/${o.id}/refund`, {}); toast.success("Refund ausgelöst"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="border border-border rounded-2xl overflow-x-auto">
      <table className="w-full text-sm min-w-[700px]">
        <thead className="bg-secondary/50 text-left text-xs"><tr><th className="p-3">Datum</th><th className="p-3">Artikel</th><th className="p-3">Gesamt</th><th className="p-3">Gebühr</th><th className="p-3">Status</th><th className="p-3">Auszahlung</th><th className="p-3">Aktion</th></tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-t border-border" data-testid={`admin-order-row-${o.id}`}>
              <td className="p-3 text-xs">{formatDate(o.created_at)}</td>
              <td className="p-3 text-xs max-w-[180px] truncate">{o.item_title}</td>
              <td className="p-3 font-semibold">{formatPrice(o.total_amount)}</td>
              <td className="p-3 text-xs">{formatPrice(o.platform_revenue)}</td>
              <td className="p-3 text-xs">{ORDER_STATUS[o.status] || o.status}</td>
              <td className="p-3 text-xs">{o.payout_status}</td>
              <td className="p-3">
                {["PAID", "SHIPPED", "DELIVERED"].includes(o.status) && o.refund_amount < o.total_amount && (
                  <button data-testid={`admin-refund-btn-${o.id}`} onClick={() => refund(o)} className="text-xs font-bold border border-destructive text-destructive rounded-full px-3 py-1.5 hover:bg-destructive/10">Refund</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DisputesTab() {
  const [disputes, setDisputes] = useState([]);
  const load = useCallback(() => { api.get("/admin/disputes").then(({ data }) => setDisputes(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const resolve = async (d, resolution) => {
    try { await api.post(`/admin/disputes/${d.id}/resolve`, { resolution, note: `Entscheidung: ${resolution}` }); toast.success("Fall entschieden"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-3">
      {disputes.length === 0 && <p className="text-sm text-muted-foreground">Keine Fälle.</p>}
      {disputes.map((d) => (
        <div key={d.id} className="border border-border rounded-2xl p-4" data-testid={`dispute-card-${d.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="font-semibold text-sm">{d.reason}</span>
            <span className="text-xs bg-secondary font-semibold px-2 py-1 rounded-md">{d.status}</span>
            <span className="text-xs text-muted-foreground ml-auto">{formatDate(d.created_at)}</span>
          </div>
          {d.details && <p className="text-sm text-muted-foreground mt-2">{d.details}</p>}
          {["OPEN", "UNDER_REVIEW"].includes(d.status) && (
            <div className="flex gap-2 mt-3">
              <button data-testid={`resolve-buyer-btn-${d.id}`} onClick={() => resolve(d, "RESOLVED_BUYER")} className="text-xs font-bold bg-primary text-white rounded-full px-4 py-2 hover:opacity-90">Käufer erstatten</button>
              <button data-testid={`resolve-seller-btn-${d.id}`} onClick={() => resolve(d, "RESOLVED_SELLER")} className="text-xs font-bold border border-border rounded-full px-4 py-2 hover:bg-secondary">Auszahlung freigeben</button>
              <button data-testid={`close-dispute-btn-${d.id}`} onClick={() => resolve(d, "CLOSED")} className="text-xs font-bold border border-border rounded-full px-4 py-2 hover:bg-secondary">Schließen</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReportsTab() {
  const [reports, setReports] = useState([]);
  const load = useCallback(() => { api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (r, action) => {
    try { await api.post(`/admin/reports/${r.id}/action`, { action }); toast.success("Erledigt"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Offene Fälle werden innerhalb von 24 Stunden bearbeitet. Erledigte Meldungen werden ausgeblendet.</p>
      {reports.length === 0 && <p className="text-sm text-muted-foreground">Keine offenen Fälle – alles erledigt. ✓</p>}
      {reports.map((r) => (
        <div key={r.id} className="border border-border rounded-2xl p-4" data-testid={`report-card-${r.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            {r.number && <span className="text-xs font-bold bg-foreground text-background px-2 py-1 rounded-md">{r.number}</span>}
            <span className="font-semibold text-sm">{({ AUTO_MODERATION: "Automatische Prüfung", BLOCKIERT: "Nutzer blockiert", UNANGEMESSEN: "Unangemessener Inhalt", BETRUG: "Betrugsverdacht", FAELSCHUNG: "Fälschungsverdacht", VERBOTENER_ARTIKEL: "Verbotener Artikel", FALSCHE_BESCHREIBUNG: "Falsche Beschreibung", SPAM: "Spam", SONSTIGES: "Sonstiges" }[r.reason] || r.reason)}</span>
            <span className="text-xs bg-secondary font-semibold px-2 py-1 rounded-md">{({ item: "Artikel", user: "Profil", message: "Nachricht", review: "Bewertung" }[r.entity_type] || r.entity_type)}: {r.entity_id.slice(0, 8)}</span>
            {r.source === "AUTO" && <span className="text-xs bg-amber-100 text-amber-800 font-semibold px-2 py-1 rounded-md">Automatisch erkannt</span>}
            {typeof r.hours_left === "number" && (
              <span data-testid={`report-sla-${r.id}`} className={`text-xs font-bold px-2 py-1 rounded-md ${r.hours_left <= 0 ? "bg-red-100 text-red-700" : r.hours_left <= 6 ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-700"}`}>
                {r.hours_left <= 0 ? `Überfällig (${Math.abs(r.hours_left).toFixed(0)} Std.)` : `Noch ${r.hours_left.toFixed(0)} Std.`}
              </span>
            )}
            <span className="text-xs text-muted-foreground ml-auto">{formatDate(r.created_at)}</span>
          </div>
          {r.details && <p className="text-sm text-muted-foreground mt-2">{r.details}</p>}
          {r.status === "OPEN" && (
            <div className="flex flex-wrap gap-2 mt-3">
              {r.entity_type === "item" && <button data-testid={`block-item-btn-${r.id}`} onClick={() => act(r, "BLOCK_ITEM")} className="text-xs font-bold bg-destructive text-white rounded-full px-4 py-2">Artikel sperren</button>}
              {r.entity_type === "user" && <button data-testid={`ban-user-btn-${r.id}`} onClick={() => act(r, "BAN_USER")} className="text-xs font-bold bg-destructive text-white rounded-full px-4 py-2">Nutzer sperren</button>}
              {r.entity_type === "message" && <button data-testid={`delete-message-btn-${r.id}`} onClick={() => act(r, "DELETE_MESSAGE")} className="text-xs font-bold bg-destructive text-white rounded-full px-4 py-2">Nachricht löschen</button>}
              {r.entity_type === "review" && <button data-testid={`delete-review-btn-${r.id}`} onClick={() => act(r, "DELETE_REVIEW")} className="text-xs font-bold bg-destructive text-white rounded-full px-4 py-2">Bewertung löschen</button>}
              <button data-testid={`dismiss-report-btn-${r.id}`} onClick={() => act(r, "DISMISS")} className="text-xs font-bold border border-border rounded-full px-4 py-2 hover:bg-secondary">Abweisen</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", {
        buyer_fee_percent: parseFloat(settings.buyer_fee_percent), buyer_fee_fixed: parseFloat(settings.buyer_fee_fixed),
        protection_days: parseInt(settings.protection_days),
        offer_reservation_minutes: parseInt(settings.offer_reservation_minutes ?? 30) });
      setSettings(data);
      toast.success("Einstellungen gespeichert");
    } catch (e) { toast.error(errText(e)); }
  };
  if (!settings) return null;
  const input = "w-full rounded-xl border border-border px-4 py-3 text-sm";
  return (
    <div className="max-w-md space-y-4">
      <div><label className="text-sm font-semibold block mb-1.5">Käufergebühr (%)</label>
        <input data-testid="setting-buyer-fee-percent" className={input} type="number" step="0.1" value={settings.buyer_fee_percent} onChange={(e) => setSettings({ ...settings, buyer_fee_percent: e.target.value })} /></div>
      <div><label className="text-sm font-semibold block mb-1.5">Käufergebühr fix (€)</label>
        <input data-testid="setting-buyer-fee-fixed" className={input} type="number" step="0.1" value={settings.buyer_fee_fixed} onChange={(e) => setSettings({ ...settings, buyer_fee_fixed: e.target.value })} /></div>
      <div><label className="text-sm font-semibold block mb-1.5">Verkäufergebühr</label>
        <p data-testid="setting-seller-fee-fixed-zero" className="w-full rounded-xl border border-border px-4 py-3 text-sm bg-secondary text-muted-foreground">0 % – fest verankert: Der Verkaufserlös gehört vollständig dem Verkäufer. KLEDIO verdient nur an Käufergebühr, Boosts und Werbung.</p></div>
      <div><label className="text-sm font-semibold block mb-1.5">Käuferschutzfrist (Tage)</label>
        <input data-testid="setting-protection-days" className={input} type="number" value={settings.protection_days} onChange={(e) => setSettings({ ...settings, protection_days: e.target.value })} /></div>
      <div><label className="text-sm font-semibold block mb-1.5">Angebots-Reservierung (Minuten)</label>
        <input data-testid="setting-reservation-minutes" className={input} type="number" value={settings.offer_reservation_minutes ?? 30} onChange={(e) => setSettings({ ...settings, offer_reservation_minutes: e.target.value })} /></div>
      <button data-testid="settings-save-btn" onClick={save} className="bg-primary text-primary-foreground font-bold px-8 py-3 rounded-full hover:opacity-90">Speichern</button>
    </div>
  );
}

function AdsAdminTab() {
  const [overview, setOverview] = useState(null);
  const [boosts, setBoosts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [ads, setAds] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/ads/overview").then(({ data }) => setOverview(data)).catch(() => {});
    api.get("/admin/ads/boosts").then(({ data }) => setBoosts(data)).catch(() => {});
    api.get("/admin/ads/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
    api.get("/admin/ads/settings").then(({ data }) => setAds(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const saveAds = async () => {
    try {
      const { data } = await api.put("/admin/ads/settings", {
        packages: ads.packages.map((p) => ({ ...p, price: parseFloat(p.price) })),
        price_tiers_enabled: ads.price_tiers_enabled,
        boost_factor: parseFloat(ads.boost_factor),
        max_boost_share: parseFloat(ads.max_boost_share),
        max_boosts_per_seller: parseInt(ads.max_boosts_per_seller),
        campaign_cpi: parseFloat(ads.campaign_cpi),
      });
      setAds(data);
      toast.success("Werbe-Einstellungen gespeichert");
    } catch (e) { toast.error(errText(e)); }
  };
  const refund = async (paymentId) => {
    const reason = window.prompt("Grund für die Rückerstattung:");
    if (!reason) return;
    try {
      await api.post("/admin/ads/refund", { payment_id: paymentId, reason });
      toast.success("Rückerstattung ausgelöst");
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const input = "w-full rounded-xl border border-border px-3 py-2 text-sm";
  return (
    <div className="space-y-6">
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ads-admin-overview">
          <StatCard label="Boost-Umsatz" value={formatPrice(overview.boost_revenue)} testid="ads-stat-boost-rev" />
          <StatCard label="Kampagnen-Umsatz" value={formatPrice(overview.campaign_revenue)} testid="ads-stat-camp-rev" />
          <StatCard label="Gesamt-Werbeumsatz" value={formatPrice(overview.total_revenue)} testid="ads-stat-total-rev" />
          <StatCard label="Boosts verkauft" value={overview.boosts_sold} testid="ads-stat-sold" />
          <StatCard label="Aktive Boosts" value={overview.active_boosts} testid="ads-stat-active-boosts" />
          <StatCard label="Aktive Kampagnen" value={overview.active_campaigns} testid="ads-stat-active-camps" />
          <StatCard label="Impressionen" value={overview.impressions_total} testid="ads-stat-impressions" />
          <StatCard label="Klicks" value={overview.clicks_total} testid="ads-stat-clicks" />
        </div>
      )}
      {ads && (
        <div className="border border-border rounded-2xl p-5 bg-card max-w-2xl" data-testid="ads-admin-settings">
          <p className="font-bold text-sm mb-3">Boost-Preise & Regeln</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ads.packages.map((p, i) => (
              <div key={p.days}><label className="text-xs font-semibold block mb-1">{p.days} Tage (€)</label>
                <input data-testid={`ads-price-${p.days}`} className={input} type="number" step="0.01" value={p.price}
                  onChange={(e) => setAds({ ...ads, packages: ads.packages.map((x, j) => j === i ? { ...x, price: e.target.value } : x) })} /></div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <div><label className="text-xs font-semibold block mb-1">Boost-Faktor</label>
              <input data-testid="ads-boost-factor" className={input} type="number" step="0.1" value={ads.boost_factor} onChange={(e) => setAds({ ...ads, boost_factor: e.target.value })} /></div>
            <div><label className="text-xs font-semibold block mb-1">Max. Feed-Anteil (0–0.5)</label>
              <input data-testid="ads-max-share" className={input} type="number" step="0.05" value={ads.max_boost_share} onChange={(e) => setAds({ ...ads, max_boost_share: e.target.value })} /></div>
            <div><label className="text-xs font-semibold block mb-1">Max. Boosts/Verkäufer</label>
              <input data-testid="ads-max-per-seller" className={input} type="number" value={ads.max_boosts_per_seller} onChange={(e) => setAds({ ...ads, max_boosts_per_seller: e.target.value })} /></div>
            <div><label className="text-xs font-semibold block mb-1">Kampagnen-CPI (€)</label>
              <input data-testid="ads-cpi" className={input} type="number" step="0.001" value={ads.campaign_cpi} onChange={(e) => setAds({ ...ads, campaign_cpi: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" data-testid="ads-tiers-enabled" className="accent-[#FF6B35]" checked={!!ads.price_tiers_enabled} onChange={(e) => setAds({ ...ads, price_tiers_enabled: e.target.checked })} />
            Preisstufen nach Artikelpreis aktivieren
          </label>
          <button data-testid="ads-settings-save" onClick={saveAds} className="mt-4 bg-primary text-primary-foreground text-sm font-bold px-6 py-2.5 rounded-full hover:opacity-90">Speichern</button>
        </div>
      )}
      <div className="border border-border rounded-2xl p-5 bg-card overflow-x-auto">
        <p className="font-bold text-sm mb-3">Boosts</p>
        <table className="w-full text-sm min-w-[650px]" data-testid="ads-admin-boosts-table">
          <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Artikel</th><th>Dauer</th><th>Preis</th><th>Status</th><th>Ende</th></tr></thead>
          <tbody>
            {boosts.map((b) => (
              <tr key={b.id} className="border-b border-border/60">
                <td className="py-2 max-w-[200px] truncate">{b.item_title}</td><td>{b.days} T</td><td>{formatPrice(b.price)}</td>
                <td>{b.status}</td><td className="text-xs">{b.end_at ? formatDate(b.end_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border border-border rounded-2xl p-5 bg-card overflow-x-auto">
        <p className="font-bold text-sm mb-3">Kampagnen</p>
        <table className="w-full text-sm min-w-[650px]" data-testid="ads-admin-campaigns-table">
          <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Artikel</th><th>Budget</th><th>Ausgegeben</th><th>Status</th><th>Ende</th></tr></thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} className="border-b border-border/60">
                <td className="py-2">{c.item_ids?.length}</td><td>{formatPrice(c.budget)}</td><td>{formatPrice(c.spent || 0)}</td>
                <td>{c.status}</td><td className="text-xs">{c.end_at ? formatDate(c.end_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AdsRefundBox onRefund={refund} />
    </div>
  );
}

function AdsRefundBox({ onRefund }) {
  const [paymentId, setPaymentId] = useState("");
  return (
    <div className="border border-border rounded-2xl p-5 bg-card max-w-md" data-testid="ads-refund-box">
      <p className="font-bold text-sm mb-2">Werbezahlung erstatten</p>
      <p className="text-xs text-muted-foreground mb-2">Nur in Ausnahmefällen (z. B. technischer Fehler). Payment-ID aus der Verkäufer-Zahlungsübersicht bzw. Datenbank.</p>
      <div className="flex gap-2">
        <input data-testid="ads-refund-payment-id" className="flex-1 rounded-xl border border-border px-3 py-2 text-sm" placeholder="Payment-ID" value={paymentId} onChange={(e) => setPaymentId(e.target.value)} />
        <button data-testid="ads-refund-btn" onClick={() => paymentId && onRefund(paymentId)} className="bg-destructive text-white text-sm font-bold px-4 py-2 rounded-full hover:opacity-90">Erstatten</button>
      </div>
    </div>
  );
}

function BadgesAdminTab() {
  const [badges, setBadges] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { api.get("/admin/badges").then(({ data }) => setBadges(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      const req = {};
      Object.entries(edit.requirements || {}).forEach(([k, v]) => { if (v !== "" && v !== null) req[k] = typeof v === "boolean" ? v : parseFloat(v); });
      await api.put(`/admin/badges/${edit.key}`, { key: edit.key, name: edit.name, description: edit.description || "",
        icon: edit.icon, priority: parseInt(edit.priority), active: edit.active, level: edit.level || "base", requirements: req });
      toast.success("Badge gespeichert");
      setEdit(null); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const REQ_LABELS = { min_sales: "Min. Verkäufe", min_rating: "Min. Bewertung", max_dispute_rate: "Max. Streitquote (0–1)",
    max_cancellation_rate: "Max. Stornoquote (0–1)", min_response_rate: "Min. Antwortquote (0–1)",
    max_response_minutes: "Max. Antwortzeit (Min.)", min_conversations: "Min. Unterhaltungen",
    min_fast_ship_rate: "Min. Schnellversand-Quote (0–1)", max_ship_hours: "Max. Versandzeit (Std.)" };
  return (
    <div className="space-y-3">
      {badges.map((b) => (
        <div key={b.key} className="border border-border rounded-2xl p-4" data-testid={`admin-badge-${b.key}`}>
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-bold text-sm">{b.name}</p>
            <span className="text-xs bg-secondary font-semibold px-2 py-1 rounded-md">{b.key}</span>
            <span className={`text-xs font-semibold px-2 py-1 rounded-md ${b.active ? "bg-accent text-white" : "bg-muted"}`}>{b.active ? "Aktiv" : "Deaktiviert"}</span>
            <span className="text-xs text-muted-foreground">{b.holder_count} Verkäufer</span>
            <button data-testid={`edit-badge-${b.key}`} onClick={() => setEdit(edit?.key === b.key ? null : { ...b })}
              className="ml-auto text-xs font-bold border border-border rounded-full px-4 py-1.5 hover:bg-secondary">{edit?.key === b.key ? "Schließen" : "Bearbeiten"}</button>
          </div>
          {edit?.key === b.key && (
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <input data-testid="badge-edit-name" className="rounded-xl border border-border px-3 py-2 text-sm" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Name" />
              <input className="rounded-xl border border-border px-3 py-2 text-sm" type="number" value={edit.priority} onChange={(e) => setEdit({ ...edit, priority: e.target.value })} placeholder="Priorität" />
              <input className="rounded-xl border border-border px-3 py-2 text-sm sm:col-span-2" value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Beschreibung" />
              {Object.keys(REQ_LABELS).map((k) => (
                <div key={k}>
                  <label className="text-xs text-muted-foreground block mb-1">{REQ_LABELS[k]}</label>
                  <input data-testid={`badge-req-${k}`} className="w-full rounded-xl border border-border px-3 py-2 text-sm" type="number" step="0.01"
                    value={edit.requirements?.[k] ?? ""} onChange={(e) => setEdit({ ...edit, requirements: { ...edit.requirements, [k]: e.target.value } })} placeholder="—" />
                </div>
              ))}
              <div className="flex items-center gap-3 sm:col-span-2">
                <label className="text-sm flex items-center gap-2"><input type="checkbox" data-testid="badge-edit-active" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Aktiv</label>
                <button data-testid="badge-save-btn" onClick={save} className="ml-auto bg-primary text-primary-foreground text-sm font-bold px-6 py-2.5 rounded-full hover:opacity-90">Speichern</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BrandProtectionTab() {
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState("");
  const load = useCallback(() => {
    api.get("/admin/brand-protection/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/moderation").then(({ data }) => setCases(data)).catch(() => {});
    api.get("/admin/brands").then(({ data }) => setBrands(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (c, action) => {
    try { await api.post(`/admin/moderation/${c.id}/action`, { action, note: action }); toast.success("Aktion ausgeführt"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const saveBrand = async (b, patch) => {
    try { await api.put(`/admin/brands/${b.id}`, { name: b.name, high_risk: b.high_risk, active: b.active, ...patch }); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const addBrand = async () => {
    try { await api.post("/admin/brands", { name: newBrand, high_risk: false, active: true }); setNewBrand(""); toast.success("Marke hinzugefügt"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const TYPE = { RISK_TERMS: "Verdächtige Begriffe", HIGH_RISK_BRAND: "Luxusmarke", COUNTERFEIT_REPORT: "Fälschungsmeldung" };
  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="brand-protection-stats">
          <StatCard label="Offene Prüfungen" value={stats.open_cases} />
          <StatCard label="Bestätigte Fälschungen" value={stats.confirmed_counterfeits} />
          <StatCard label="Entfernte Artikel" value={stats.removed_items} />
          <StatCard label="In Review" value={stats.review_required_items} />
          <StatCard label="KLEDIO geprüft" value={stats.authenticated_items} />
          <StatCard label="High-Risk aktiv" value={stats.high_risk_items} />
          <StatCard label="Strikes gesamt" value={stats.strikes_total} />
          <StatCard label="Gesperrte Verkäufer" value={stats.suspended_sellers} />
        </div>
      )}
      <div>
        <h3 className="font-bold text-base mb-3">Prüffälle</h3>
        <div className="space-y-3">
          {cases.length === 0 && <p className="text-sm text-muted-foreground">Keine Fälle.</p>}
          {cases.map((c) => (
            <div key={c.id} className="border border-border rounded-2xl p-4" data-testid={`mod-case-${c.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-bold">{TYPE[c.type] || c.type}</span>
                <span className="text-xs bg-secondary font-semibold px-2 py-1 rounded-md">{c.status}</span>
                <span className="text-xs text-muted-foreground">{c.reason}</span>
                <span className="text-xs text-muted-foreground ml-auto">{formatDate(c.created_at)}</span>
              </div>
              {c.item && <p className="text-xs text-muted-foreground mt-1.5">Artikel: <a href={`/artikel/${c.item.id}`} className="text-accent hover:underline">{c.item.title}</a> · {formatPrice(c.item.price)} · Status {c.item.status} · Verkäufer @{c.seller?.username} ({c.seller_strikes} Strikes)</p>}
              {c.documents?.length > 0 && (
                <p className="text-xs mt-1">Nachweise: {c.documents.flatMap((d) => d.image_ids).map((id, i) => (
                  <a key={id} href={`${API}/authenticity-docs/${id}/medium`} target="_blank" rel="noreferrer" className="text-accent hover:underline mr-2">Dokument {i + 1}</a>))}</p>
              )}
              {["OPEN", "AUTHENTICITY_REQUIRED"].includes(c.status) && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <button data-testid={`case-approve-${c.id}`} onClick={() => act(c, "APPROVE")} className="text-xs font-bold bg-primary text-white rounded-full px-3.5 py-2">Freigeben</button>
                  <button data-testid={`case-verify-${c.id}`} onClick={() => act(c, "VERIFY_AUTHENTIC")} className="text-xs font-bold border-2 border-accent text-accent rounded-full px-3.5 py-2">Authentisch ✓</button>
                  <button data-testid={`case-proof-${c.id}`} onClick={() => act(c, "REQUEST_PROOF")} className="text-xs font-bold border border-border rounded-full px-3.5 py-2">Nachweise anfordern</button>
                  <button data-testid={`case-warn-${c.id}`} onClick={() => act(c, "WARN_SELLER")} className="text-xs font-bold border border-border rounded-full px-3.5 py-2">Verwarnen</button>
                  <button data-testid={`case-reject-${c.id}`} onClick={() => act(c, "REJECT")} className="text-xs font-bold border border-destructive text-destructive rounded-full px-3.5 py-2">Ablehnen</button>
                  <button data-testid={`case-counterfeit-${c.id}`} onClick={() => act(c, "REMOVE_COUNTERFEIT")} className="text-xs font-bold bg-destructive text-white rounded-full px-3.5 py-2">Fälschung bestätigen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-bold text-base mb-3">Marken</h3>
        <div className="flex gap-2 mb-3">
          <input data-testid="new-brand-input" className="rounded-xl border border-border px-3 py-2 text-sm" placeholder="Neue Marke…" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} />
          <button data-testid="add-brand-btn" onClick={addBrand} disabled={newBrand.length < 2} className="bg-primary text-white text-xs font-bold px-4 rounded-xl disabled:opacity-40">Hinzufügen</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {brands.map((b) => (
            <div key={b.id} className={`flex items-center gap-2 border rounded-full px-3 py-1.5 text-xs ${b.active ? "border-border" : "border-border opacity-40"}`} data-testid={`brand-chip-${b.id}`}>
              <span className="font-semibold">{b.name}</span>
              <button onClick={() => saveBrand(b, { high_risk: !b.high_risk })} title="High-Risk umschalten"
                className={`font-bold px-1.5 py-0.5 rounded ${b.high_risk ? "bg-accent text-white" : "bg-secondary"}`}>HR</button>
              <button onClick={() => saveBrand(b, { active: !b.active })} className="text-muted-foreground hover:text-destructive">{b.active ? "✕" : "↺"}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then(({ data }) => setLogs(data)).catch(() => {}); }, []);
  return (
    <div className="border border-border rounded-2xl overflow-x-auto">
      <table className="w-full text-sm min-w-[600px]" data-testid="audit-log-table">
        <thead className="bg-secondary/50 text-left text-xs"><tr><th className="p-3">Zeit</th><th className="p-3">Aktion</th><th className="p-3">Entität</th><th className="p-3">Nutzer</th></tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-border">
              <td className="p-3 text-xs">{new Date(l.created_at).toLocaleString("de-DE")}</td>
              <td className="p-3 text-xs font-semibold">{l.action}</td>
              <td className="p-3 text-xs">{l.entity} · {String(l.entity_id).slice(0, 8)}</td>
              <td className="p-3 text-xs">{String(l.user_id).slice(0, 8)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DemoContentTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetPw, setResetPw] = useState("");
  const [diag, setDiag] = useState(null);
  const runDiagnose = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/stripe/diagnose"); setDiag(data); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const platformReset = async () => {
    if (!window.confirm("Letzte Warnung: Alle Nutzer, Artikel und Finanzdaten werden endgültig gelöscht. Fortfahren?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/platform/reset", { confirmation: resetConfirm.trim(), password: resetPw });
      const d = data.deleted;
      toast.success(`Zurückgesetzt: ${d.users} Nutzer, ${d.items} Artikel, ${d.orders} Bestellungen gelöscht. Behalten: ${data.kept.demo_items} Demo-Artikel, ${data.kept.staff} Admin(s).`);
      setResetConfirm(""); setResetPw(""); load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const load = useCallback(() => {
    api.get("/admin/demo/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);
  const seed = async () => {
    setBusy(true);
    try { await api.post("/admin/demo/seed"); toast.success("Demo-Erstellung gestartet – läuft im Hintergrund"); load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Wirklich alle Demo-Verkäufer, -Artikel und -Bewertungen löschen?")) return;
    setBusy(true);
    try { const { data } = await api.delete("/admin/demo"); toast.success(`Gelöscht: ${data.items} Artikel, ${data.users} Verkäufer, ${data.reviews} Bewertungen`); load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const s = status?.status;
  const running = s?.state === "RUNNING";
  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-demo-tab">
      <div className="border border-border rounded-2xl p-5 bg-card">
        <h3 className="font-bold">Demo-Inhalte</h3>
        <p className="text-sm text-muted-foreground mt-1">Erstellt 12 realistische Verkäuferprofile mit Profilbildern und 57 Artikel mit Produktfotos quer durch alle Kategorien. Demo-Artikel sind sichtbar, aber nicht kaufbar (Checkout und Angebote werden blockiert).</p>
        <div className="mt-4 flex items-center gap-4 text-sm">
          <span data-testid="demo-count-users"><b>{status?.demo_users ?? "–"}</b> Demo-Verkäufer</span>
          <span data-testid="demo-count-items"><b>{status?.demo_items ?? "–"}</b> Demo-Artikel</span>
        </div>
        {s && (
          <div className="mt-3 text-sm" data-testid="demo-seed-status">
            {running && <span className="text-amber-600 font-semibold">⏳ Läuft: {s.progress}/{s.total} – {s.message}</span>}
            {s.state === "DONE" && <span className="text-green-600 font-semibold">✓ {s.message}</span>}
            {s.state === "ERROR" && <span className="text-red-600 font-semibold">Fehler: {s.error}</span>}
          </div>
        )}
        <div className="mt-5 flex gap-3">
          <button onClick={seed} disabled={busy || running || status?.demo_users > 0} data-testid="demo-seed-btn"
            className="bg-foreground text-white text-sm font-bold px-5 py-2.5 rounded-full disabled:opacity-40">Demo-Inhalte erstellen</button>
          <button onClick={remove} disabled={busy || running || !status?.demo_users} data-testid="demo-delete-btn"
            className="border border-red-300 text-red-600 text-sm font-bold px-5 py-2.5 rounded-full disabled:opacity-40">Demo-Inhalte löschen</button>
        </div>
      </div>
      <div className="border border-border rounded-2xl p-5 bg-card" data-testid="stripe-diagnose-card">
        <h3 className="font-bold">Stripe-Status</h3>
        <p className="text-sm text-muted-foreground mt-1">Prüft API-Key, Connect-Freischaltung (Verkäufer-Verifizierung & Auszahlungen) und Webhook-Konfiguration.</p>
        <button onClick={runDiagnose} disabled={busy} data-testid="stripe-diagnose-btn"
          className="mt-4 border border-border text-sm font-bold px-5 py-2.5 rounded-full hover:bg-secondary disabled:opacity-40">Stripe-Verbindung prüfen</button>
        {diag && (
          <div className="mt-4 space-y-2 text-sm" data-testid="stripe-diagnose-result">
            <p>Modus: <b className={diag.mode === "LIVE" ? "text-green-600" : "text-amber-600"}>{diag.mode}</b></p>
            <p>{diag.key_valid ? "✅" : "❌"} API-Key gültig</p>
            <p>{diag.connect_enabled ? "✅" : "❌"} Stripe Connect aktiviert (nötig für Verifizierung & Auszahlungen)</p>
            <p>{diag.webhook_secret_set ? "✅" : "❌"} Webhook-Secret gesetzt</p>
            {diag.hints.map((h, i) => <p key={i} className="text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">{h}</p>)}
          </div>
        )}
      </div>
      <div className="border border-red-300 rounded-2xl p-5 bg-red-50/50" data-testid="platform-reset-card">
        <h3 className="font-bold text-red-700">Gefahrenzone: Plattform zurücksetzen</h3>
        <p className="text-sm text-muted-foreground mt-1">Löscht unwiderruflich alle Nutzerkonten, Artikel, Bestellungen, Zahlungsdaten, Nachrichten, Bewertungen und Werbekampagnen. <b>Erhalten bleiben:</b> Admin-Konten, Demo-Inhalte, Kategorien, Marken und Plattform-Einstellungen.</p>
        <div className="mt-4 space-y-3 max-w-sm">
          <input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder={'Zur Bestätigung „KLEDIO ZURÜCKSETZEN" eingeben'}
            className="w-full border border-border rounded-xl px-3.5 py-2.5 text-sm bg-white" data-testid="reset-confirm-input" />
          <input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Dein Admin-Passwort"
            className="w-full border border-border rounded-xl px-3.5 py-2.5 text-sm bg-white" data-testid="reset-password-input" />
          <button onClick={platformReset} disabled={busy || resetConfirm.trim() !== "KLEDIO ZURÜCKSETZEN" || !resetPw} data-testid="reset-submit-btn"
            className="bg-red-600 text-white text-sm font-bold px-5 py-2.5 rounded-full disabled:opacity-40">Plattform unwiderruflich zurücksetzen</button>
        </div>
      </div>
    </div>
  );
}

function GiveawayTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/giveaway/entries").then(({ data }) => setData(data)).catch((e) => toast.error(errText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="h-40 bg-muted rounded-2xl animate-pulse" />;
  const { stats, entries, prizes } = data;
  const drawnCount = (key) => entries.filter((e) => e.prize === key).length;
  const platformLabel = { tiktok: "TikTok", instagram: "Instagram", facebook: "Facebook" };
  const statusBadge = { SUBMITTED: ["Im Lostopf", "bg-secondary text-foreground/70"], WINNER: ["Gewinner 🎉", "bg-green-100 text-green-700"], DISQUALIFIED: ["Ausgeschlossen", "bg-red-100 text-red-700"] };

  const draw = async (key) => {
    const prize = prizes.find((p) => p.key === key);
    const remaining = prize.total - drawnCount(key);
    if (remaining <= 0) return toast.info("Alle Gewinne dieser Kategorie sind bereits verlost.");
    if (!window.confirm(`Jetzt ${remaining}× „${prize.label}" per Zufall unter allen gültigen Teilnahmen ziehen?`)) return;
    const beforeEnd = new Date() < new Date("2027-01-31T23:59:59+01:00");
    if (beforeEnd && !window.confirm("Achtung: Das Gewinnspiel läuft noch bis 31.01.2027. Trotzdem jetzt schon ziehen?")) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/giveaway/draw", { prize_key: key, count: remaining, force: beforeEnd });
      toast.success(`${res.winners.length} Gewinner gezogen: ${res.winners.map((w) => w.username).join(", ")}`);
      load();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const disqualify = async (id) => {
    if (!window.confirm("Diese Teilnahme wirklich ausschließen?")) return;
    try { await api.post(`/admin/giveaway/entries/${id}/disqualify`); toast.success("Teilnahme ausgeschlossen"); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-giveaway-tab">
      <div className="grid sm:grid-cols-3 gap-4">
        {[["Teilnahmen", stats.total], ["Gewinner gezogen", stats.winners], ["Ausgeschlossen", stats.disqualified]].map(([l, v]) => (
          <div key={l} className="bg-white border border-border rounded-2xl p-4">
            <p className="text-xs text-muted-foreground font-semibold">{l}</p>
            <p className="text-2xl font-bold mt-1">{v}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-border rounded-2xl p-5">
        <p className="font-bold text-sm mb-1">Gewinner ziehen (Losverfahren)</p>
        <p className="text-xs text-muted-foreground mb-4">Zieht per Zufall aus allen gültigen Teilnahmen. Gewinner werden automatisch per Benachrichtigung informiert.</p>
        <div className="flex flex-wrap gap-2.5">
          {prizes.map((p) => (
            <button key={p.key} data-testid={`giveaway-draw-${p.key}`} onClick={() => draw(p.key)} disabled={busy}
              className="border border-border bg-secondary/50 hover:border-accent/60 text-sm font-semibold px-4 py-2.5 rounded-full disabled:opacity-50">
              {p.label} · {drawnCount(p.key)}/{p.total} gezogen
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs text-muted-foreground">
              <tr>{["Datum", "Nutzer", "Plattform", "Beitrag", "Screenshot", "Status", "Gewinn", ""].map((h) => <th key={h} className="text-left font-semibold px-4 py-2.5">{h}</th>)}</tr>
            </thead>
            <tbody data-testid="giveaway-entries-table">
              {entries.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Noch keine Teilnahmen.</td></tr>}
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-border/60">
                  <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(e.created_at)}</td>
                  <td className="px-4 py-2.5"><span className="font-semibold">{e.username}</span><br /><span className="text-xs text-muted-foreground">{e.email}</span></td>
                  <td className="px-4 py-2.5">{platformLabel[e.platform] || e.platform}</td>
                  <td className="px-4 py-2.5">{e.post_url ? <a href={e.post_url} target="_blank" rel="noreferrer" className="text-accent underline underline-offset-2">Link</a> : "—"}</td>
                  <td className="px-4 py-2.5"><a href={`${API}/admin/giveaway/screenshot/${e.id}`} target="_blank" rel="noreferrer" className="text-accent underline underline-offset-2">Ansehen</a></td>
                  <td className="px-4 py-2.5"><span className={`text-[11px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${statusBadge[e.status]?.[1]}`}>{statusBadge[e.status]?.[0] || e.status}</span></td>
                  <td className="px-4 py-2.5 text-xs">{e.prize ? prizes.find((p) => p.key === e.prize)?.label : "—"}</td>
                  <td className="px-4 py-2.5">{e.status === "SUBMITTED" && <button onClick={() => disqualify(e.id)} className="text-xs font-semibold text-red-600 hover:underline">Ausschließen</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const PANEL = ["SUPPORT", "MODERATOR", "ADMIN", "SUPER_ADMIN"];
const MOD = ["MODERATOR", "ADMIN", "SUPER_ADMIN"];
const ADM = ["ADMIN", "SUPER_ADMIN"];

const NAV = [
  { group: "Support", items: [
    { key: "overview", label: "Dashboard", icon: LayoutDashboard, roles: PANEL },
    { key: "tickets", label: "Tickets", icon: Inbox, roles: PANEL },
    { key: "emails", label: "E-Mails", icon: Mail, roles: PANEL },
    { key: "tasks", label: "Aufgaben", icon: ListTodo, roles: PANEL },
    { key: "customers", label: "Kunden", icon: UserSearch, roles: PANEL },
  ]},
  { group: "Moderation", items: [
    { key: "reports", label: "Inhaltsmoderation", icon: Flag, roles: MOD },
    { key: "disputes", label: "Disputes", icon: Scale, roles: MOD },
    { key: "brandprotection", label: "Markenschutz", icon: ShieldCheck, roles: MOD },
  ]},
  { group: "Marktplatz", items: [
    { key: "stats", label: "Statistiken", icon: BarChart3, roles: MOD },
    { key: "users", label: "Nutzer", icon: UserCog, roles: MOD },
    { key: "orders", label: "Bestellungen", icon: Package, roles: MOD },
    { key: "tax", label: "Steuern", icon: Landmark, roles: ADM },
  ]},
  { group: "Plattform", items: [
    { key: "employees", label: "Mitarbeiter", icon: UsersRound, roles: ADM },
    { key: "settings", label: "Gebühren", icon: Percent, roles: ADM },
    { key: "badges", label: "Abzeichen", icon: Award, roles: ADM },
    { key: "ads", label: "Werbung", icon: Megaphone, roles: ADM },
    { key: "giveaway", label: "Gewinnspiel", icon: Gift, roles: ADM },
    { key: "content", label: "Inhalte & Recht", icon: FileText, roles: ADM },
    { key: "audit", label: "Audit-Log", icon: History, roles: ADM },
    { key: "demo", label: "Demo-Inhalte", icon: FlaskConical, roles: ADM },
  ]},
];

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("overview");
  const [ticketJump, setTicketJump] = useState(null);
  if (!user) return null;
  const allowed = (item) => {
    if (!item.roles.includes(user.role)) return false;
    if (user.role !== "SUPER_ADMIN" && (user.panel_perms || {})[item.key] === false) return false;
    return true;
  };
  const go = (key) => { setTicketJump(null); setSection(key); };
  const openTicket = (id) => { setTicketJump(id); setSection("tickets"); };

  const CONTENT = {
    overview: <PanelOverview go={go} />,
    tickets: <PanelTickets me={user} key={ticketJump || "tickets"} initialSelected={ticketJump} />,
    emails: <PanelEmails />,
    tasks: <PanelTasks me={user} />,
    customers: <PanelCustomers openTicket={openTicket} />,
    employees: <PanelEmployees me={user} />,
    reports: <ReportsTab />,
    disputes: <DisputesTab />,
    brandprotection: <BrandProtectionTab />,
    stats: <DashboardTab />,
    users: <UsersTab />,
    orders: <OrdersTab />,
    tax: <TaxTab />,
    settings: <SettingsTab />,
    badges: <BadgesAdminTab />,
    ads: <AdsAdminTab />,
    giveaway: <GiveawayTab />,
    content: <ContentTab />,
    audit: <AuditTab />,
    demo: <DemoContentTab />,
  };
  const current = NAV.flatMap((g) => g.items).find((i) => i.key === section);

  return (
    <div className="min-h-screen bg-background">
      {/* Panel-Topbar */}
      <header className="sticky top-0 z-40 bg-[#111111] text-white" data-testid="panel-topbar">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-5 h-14 flex items-center gap-3">
          <span className="w-8 h-8 rounded-xl bg-accent flex items-center justify-center"><LayoutDashboard className="w-4 h-4 text-white" /></span>
          <div className="leading-tight">
            <p className="font-display font-extrabold text-sm tracking-tight">KLEDIO Panel</p>
            <p className="text-[10px] text-white/50">{user.name || user.username} · {ROLE_LABEL[user.role] || user.role}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold text-white/80 hover:text-white px-3 py-2 rounded-full hover:bg-white/10" data-testid="panel-to-site-btn">
              <ExternalLink className="w-3.5 h-3.5" /> Zur Website
            </Link>
            <button onClick={async () => { await logout(); nav("/anmelden"); }} data-testid="panel-logout-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-accent text-white px-3.5 py-2 rounded-full hover:opacity-90">
              <LogOut className="w-3.5 h-3.5" /> Abmelden
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-[1500px] mx-auto px-3 sm:px-5 py-5">
      <div className="flex gap-5">
        {/* Sidebar Desktop */}
        <aside className="hidden lg:block w-56 shrink-0" data-testid="panel-sidebar">
          <div className="sticky top-[76px] space-y-5">
            {NAV.map((g) => {
              const items = g.items.filter(allowed);
              if (!items.length) return null;
              return (
                <div key={g.group}>
                  <p className="text-[10px] font-black tracking-[0.15em] uppercase text-muted-foreground px-2 mb-1.5">{g.group}</p>
                  <div className="space-y-0.5">
                    {items.map((i) => (
                      <button key={i.key} onClick={() => go(i.key)} data-testid={`panel-nav-${i.key}`}
                        className={`w-full flex items-center gap-2.5 text-sm px-2.5 py-2 rounded-xl transition-colors ${section === i.key ? "bg-[#111111] text-white font-bold" : "text-foreground/70 hover:bg-secondary/70 font-semibold"}`}>
                        <i.icon className="w-4 h-4 shrink-0" /> {i.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>
        {/* Main */}
        <div className="flex-1 min-w-0">
          {/* Mobile nav */}
          <div className="lg:hidden mb-4 -mx-3 px-3 overflow-x-auto no-scrollbar">
            <div className="flex gap-1.5 w-max" data-testid="panel-mobile-nav">
              {NAV.flatMap((g) => g.items).filter(allowed).map((i) => (
                <button key={i.key} onClick={() => go(i.key)}
                  className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full whitespace-nowrap ${section === i.key ? "bg-[#111111] text-white" : "bg-secondary/70 text-foreground/70"}`}>
                  <i.icon className="w-3.5 h-3.5" /> {i.label}
                </button>
              ))}
            </div>
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4" data-testid="panel-section-title">{current?.label}</h2>
          {CONTENT[section]}
        </div>
      </div>
      </div>
    </div>
  );
}
